'use client'

import React, { useMemo } from 'react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { ResizableTable, type ColumnDef } from '../ResizableTable'

// raw_entries.original_payload / .parsed_payload are flat (1-level)
// JSON objects — original is exactly what the submitter sent, parsed is the
// agentic-enrichment reconstruction. Values are rendered as-is; anything
// non-primitive (shouldn't normally occur at this depth) falls back to
// JSON.stringify rather than crashing on [object Object].
function formatValue(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export function PayloadWidget({
  originalPayload,
  parsedPayload,
  theme,
}: {
  originalPayload?: Record<string, unknown> | null
  parsedPayload?: Record<string, unknown> | null
  theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  const hasOriginal = !!originalPayload && Object.keys(originalPayload).length > 0
  const hasParsed = !!parsedPayload && Object.keys(parsedPayload).length > 0
  const bothPresent = hasOriginal && hasParsed

  // Union of keys, original's own order first, then any parsed-only extras.
  const keys = useMemo(() => {
    const seen = new Set<string>()
    const ordered: string[] = []
    for (const k of Object.keys(originalPayload ?? {})) {
      if (!seen.has(k)) { seen.add(k); ordered.push(k) }
    }
    for (const k of Object.keys(parsedPayload ?? {})) {
      if (!seen.has(k)) { seen.add(k); ordered.push(k) }
    }
    return ordered
  }, [originalPayload, parsedPayload])

  if (!hasOriginal && !hasParsed) {
    return <div className={`px-3 py-4 text-center text-[12px] ${c.faint}`}>Sem payload</div>
  }

  const diffColor = 'text-warning'

  // ResizableTable (same drag-to-resize + horizontal pan/scrollbar every
  // other table in the app uses) instead of a plain <table> — a bare
  // `w-full` table has no way to keep Original/Parsed from either shoving
  // each other off-width or getting silently clipped once a payload value
  // (an inlined HTML email body, say) is long enough to want more room than
  // its column has. Columns start reasonably wide but are user-resizable,
  // and the table pans/scrolls horizontally once dragged wider than the
  // panel — even inside the widget's own maximize modal.
  const columns: ColumnDef[] = [
    { key: 'field', label: 'Field', initialWidth: 140, minWidth: 80 },
    ...(hasOriginal ? [{ key: 'original', label: 'Original', initialWidth: 340, minWidth: 160 }] : []),
    ...(hasParsed ? [{ key: 'parsed', label: 'Parsed', initialWidth: 340, minWidth: 160 }] : []),
  ]

  return (
    <div className={`overflow-hidden rounded border ${c.line}`}>
      <ResizableTable
        columns={columns}
        theme={theme}
        maximizable={false}
        tableClassName="w-full text-[12px]"
        theadClassName={theme === 'dark' ? 'bg-card border-b border-border' : 'bg-secondary border-b border-border'}
      >
        {keys.map((key) => {
          const ov = formatValue(originalPayload?.[key])
          const pv = formatValue(parsedPayload?.[key])
          const differs = bothPresent && ov !== pv
          return (
            <tr key={key} className={`border-b last:border-0 ${c.line}`}>
              <td className={`px-3 py-1.5 align-top font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${c.faint}`}>
                {key}
              </td>
              {hasOriginal && (
                <td className={`px-3 py-1.5 align-top break-words font-mono text-[12px] ${c.text}`}>
                  {ov ?? <span className={c.faint}>—</span>}
                </td>
              )}
              {hasParsed && (
                <td className={`px-3 py-1.5 align-top break-words font-mono text-[12px] ${differs ? diffColor : c.text}`}>
                  {pv ?? <span className={c.faint}>—</span>}
                </td>
              )}
            </tr>
          )
        })}
      </ResizableTable>
    </div>
  )
}
